/**
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 */

/** @typedef {'selection'|'filtered'|'all'|'empty_or_low'} SdeBulkScope */

/**
 * @param {SdeRow[]} rows
 * @param {SdeRow[]} filteredRows
 * @param {number[]} selectedIndices
 * @param {SdeBulkScope} scope
 * @returns {SdeRow[]}
 */
export function resolveBulkScope(rows, filteredRows, selectedIndices, scope) {
    switch (scope) {
        case "selection":
            return rows.filter((row) => selectedIndices.includes(row.rowIndex));
        case "filtered":
            return filteredRows;
        case "empty_or_low":
            return filteredRows;
        case "all":
        default:
            return rows;
    }
}

/**
 * @param {unknown} value
 */
function isEmptyValue(value) {
    return value === null || value === undefined || value === "";
}

/**
 * @param {SdeRow[]} rows
 * @param {SdeRow[]} filteredRows
 * @param {number[]} selectedIndices
 * @param {string} fieldKey
 * @param {unknown} value
 * @param {SdeBulkScope} scope
 * @returns {SdeRow[]}
 */
export function bulkUpdateFieldRows(
    rows,
    filteredRows,
    selectedIndices,
    fieldKey,
    value,
    scope
) {
    const targetRows = resolveBulkScope(rows, filteredRows, selectedIndices, scope);
    const targetIndexSet = new Set(targetRows.map((row) => row.rowIndex));
    return rows.map((row) => {
        if (!targetIndexSet.has(row.rowIndex)) {
            return row;
        }
        const field = row.fields[fieldKey];
        if (field?.deleted) {
            return row;
        }
        if (field && scope === "empty_or_low") {
            const hasValue = !isEmptyValue(field.value);
            const isLow = field.confidence?.level === "low";
            if (hasValue && !isLow) {
                return row;
            }
        }
        // Rows built from CSV/AI data only contain mapped fields: create the
        // field when missing so bulk edit also works on columns absent from
        // the source file.
        return {
            ...row,
            fields: {
                ...row.fields,
                [fieldKey]: {
                    ...(field || {}),
                    key: fieldKey,
                    value,
                    syncError: null,
                    confidence: { score: null, level: "manual" },
                },
            },
            syncStatus: row.syncStatus === "error" ? "pending" : row.syncStatus,
        };
    });
}

/**
 * @param {SdeRow[]} rows
 * @returns {number}
 */
export function countRowsWithSuggestions(rows) {
    return rows.filter((row) =>
        Object.values(row.fields || {}).some(
            (field) =>
                field?.confidence?.suggestion &&
                field.value !== field.confidence.suggestion &&
                !field.deleted
        )
    ).length;
}

/**
 * @param {SdeRow[]} rows
 * @returns {SdeRow[]}
 */
export function applyAllSuggestionsToRows(rows) {
    return rows.map((row) => {
        const fields = { ...row.fields };
        let changed = false;
        Object.entries(fields).forEach(([key, field]) => {
            const suggestion = field?.confidence?.suggestion;
            if (
                suggestion &&
                field.value !== suggestion &&
                !field.deleted
            ) {
                fields[key] = {
                    ...field,
                    key,
                    value: suggestion,
                    confidence: {
                        ...field.confidence,
                        level: "manual",
                    },
                };
                changed = true;
            }
        });
        return changed ? { ...row, fields } : row;
    });
}

export {
    applySyncResultsToRows,
    summarizeSyncPayload,
} from "@/utils/sde-sync-errors";

/** @deprecated Use applySyncResultsToRows */
export { applySyncResultsToRows as markRowsFromSyncPayload } from "@/utils/sde-sync-errors";

/**
 * @param {SdeRow[]} rows
 * @returns {boolean}
 */
export function hasPendingImportRows(rows) {
    return rows.some((row) => !row.excluded && row.syncStatus !== "synced");
}
